/**
 * Per-recipient watermark overlay — appears on every screen of the live UI.
 * Per spec §3 + §20: forensic trace, not a primary mitigation; visible at low alpha
 * so it survives screenshots. Driven by `watermark_mode` in 00 Global Setup.xlsx.
 *
 * Modes:
 *   0 — no watermark (renders null)
 *   1 — user@tenant · YYYY.MM.DD (text overlay; the default behavior)
 *   2 — custom brand image (img overlay; src from watermark_url, or
 *       /api/global/watermark when only watermark_filename is set)
 *
 * Note: per Reporting_v4_Spec.md §5.18, EXPORT artifacts (PDF/Excel/PNG)
 * carry a per-export forensic watermark hash that is independent of this
 * mode. Editing watermark_mode does not affect those exports.
 */
import { useSetupCampaigns } from '@state/useSetupCampaigns';
import { useCdxIdentity } from '@state/useCdxIdentity';
import { assetUrl } from '@lib/apiClient';

function todayDots(): string {
  // YYYY.MM.DD per William's instruction
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${dd}`;
}

const baseStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: 1000,
};

export function WatermarkOverlay() {
  const global = useSetupCampaigns((s) => s.global);
  // Per-recipient identity from the signed URL → cookie → bootAuth → window.CDX_IDENTITY,
  // the same source Header uses. (Previously called useUser()/`/api/auth/me`, the dormant
  // Entra-bearer scaffold, which 401'd on every page and silently fell the watermark back
  // to the generic 'user@tenant' placeholder.)
  const identity = useCdxIdentity();

  const mode = global.watermarkMode;

  if (mode === 0) return null;

  if (mode === 2) {
    const url = global.watermark.url?.trim();
    const filename = global.watermark.filename?.trim();
    const src = url || (filename ? assetUrl('/api/global/watermark') : '');
    if (!src) {
      // Mode 2 selected but no source — fall through to mode 1 text so the
      // page isn't silently un-watermarked.
      return <ModeOneOverlay user={identity?.email ?? 'user@tenant'} />;
    }
    return (
      <div aria-hidden="true" style={baseStyle}>
        <img
          src={src}
          alt=""
          style={{
            maxWidth: '60vw',
            maxHeight: '60vh',
            opacity: 0.08,
            transform: 'rotate(-30deg)',
            objectFit: 'contain',
          }}
        />
      </div>
    );
  }

  // Default + mode 1
  return <ModeOneOverlay user={identity?.email ?? 'user@tenant'} />;
}

function ModeOneOverlay({ user }: { user: string }) {
  return (
    <div aria-hidden="true" style={baseStyle}>
      <div
        style={{
          color: 'var(--cdx-fg-muted, #666)',
          opacity: 0.06,
          fontSize: '8rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          transform: 'rotate(-30deg)',
          whiteSpace: 'nowrap',
          letterSpacing: '0.04em',
        }}
      >
        {user} · {todayDots()}
      </div>
    </div>
  );
}
